// AGC: Lot Notes Service
// AGC: CRUD operations for notes with media attachments
// AGC: Handles Firestore operations and integrates with media storage

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  getDoc,
  Timestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore'
import { db } from './firebase-auth'
import { uploadMedia, deleteMedia } from './mediaStorageService'
import type { 
  LotNote, 
  CreateNoteRequest, 
  UpdateNoteRequest, 
  MediaUploadProgress,
  TimelineEntry
} from '../types/lotNotes'
import type { Task } from '../types'
import { v4 as uuidv4 } from 'uuid'

const NOTES_COLLECTION = 'lot_notes'

/**
 * AGC: Create a new lot note with optional media
 */
export const createNote = async (
  request: CreateNoteRequest,
  userId: string,
  userName: string,
  userEmail: string,
  onProgress?: (progress: MediaUploadProgress) => void
): Promise<LotNote> => {
  try {
    const noteId = uuidv4()
    const now = new Date().toISOString()
    
    let mediaUrl: string | undefined
    let mediaThumbnailUrl: string | undefined
    let mediaDuration: number | undefined
    let mediaSize: number | undefined
    
    // AGC: Upload media if file provided (any type, including text notes with attachments)
    if (request.file) {
      const mediaType = request.attachmentType || request.type
      console.log(`📤 AGC: Uploading ${mediaType} file...`)
      const uploadResult = await uploadMedia(
        request.file,
        mediaType as import('../types/lotNotes').LotNoteType,
        userId,
        noteId,
        onProgress
      )
      
      mediaUrl = uploadResult.url
      mediaThumbnailUrl = uploadResult.thumbnailUrl
      mediaDuration = uploadResult.duration
      mediaSize = uploadResult.size
      
      console.log('✅ AGC: Media uploaded successfully')
    }
    
    // AGC: Create note document — use empty string fallback for content to avoid
    // AGC: Firestore rejecting undefined values (photo/audio notes have no text content)
    const noteData: Record<string, unknown> = {
      userId,
      userName,
      userEmail,
      type: request.type,
      content: request.content || '',
      lotIds: request.lotIds,
      primaryLotId: request.lotIds[0],
      taggedUserIds: request.taggedUserIds || [],
      createdAt: now,
      updatedAt: now,
      isOffline: false,
    }

    // AGC: Only set optional fields when defined — Firestore rejects undefined values
    if (mediaUrl) noteData.mediaUrl = mediaUrl
    if (mediaThumbnailUrl) noteData.mediaThumbnailUrl = mediaThumbnailUrl
    if (mediaDuration !== undefined) noteData.mediaDuration = mediaDuration
    if (mediaSize !== undefined) noteData.mediaSize = mediaSize
    if (request.gpsLocation) noteData.gpsLocation = request.gpsLocation

    // AGC: Save to Firestore
    const docRef = await addDoc(collection(db, NOTES_COLLECTION), noteData)
    
    const note = {
      id: docRef.id,
      ...noteData,
    } as LotNote
    
    console.log('💾 AGC: Note saved to Firestore:', note.id)
    
    return note
  } catch (error) {
    console.error('AGC: Failed to create note', error)
    throw error
  }
}

/**
 * AGC: Get all notes for a specific lot
 */
export const getNotesByLot = async (lotId: string): Promise<LotNote[]> => {
  try {
    // AGC: Query without orderBy to avoid needing a composite index
    // AGC: (array-contains + orderBy requires Firestore composite index).
    // AGC: Sort in JS instead — note volumes per lot are small.
    const q = query(
      collection(db, NOTES_COLLECTION),
      where('lotIds', 'array-contains', lotId)
    )

    const querySnapshot = await getDocs(q)

    const notes: LotNote[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as LotNote))

    // AGC: Sort newest first in JS
    notes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    console.log(`📋 AGC: Retrieved ${notes.length} notes for lot ${lotId}`)

    return notes
  } catch (error) {
    console.error('AGC: Failed to get notes by lot', error)
    throw error
  }
}

/**
 * AGC: Get all notes for a user
 */
export const getNotesByUser = async (userId: string): Promise<LotNote[]> => {
  try {
    const q = query(
      collection(db, NOTES_COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    
    const notes: LotNote[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as LotNote))
    
    console.log(`📋 AGC: Retrieved ${notes.length} notes for user ${userId}`)
    
    return notes
  } catch (error) {
    console.error('AGC: Failed to get notes by user', error)
    throw error
  }
}

/**
 * AGC: Get a single note by ID
 */
export const getNoteById = async (noteId: string): Promise<LotNote | null> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId)
    const docSnap = await getDoc(docRef)
    
    if (!docSnap.exists()) {
      return null
    }
    
    return {
      id: docSnap.id,
      ...docSnap.data()
    } as LotNote
  } catch (error) {
    console.error('AGC: Failed to get note by ID', error)
    throw error
  }
}

/**
 * AGC: Update note content and metadata
 */
export const updateNote = async (
  noteId: string,
  updates: UpdateNoteRequest
): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId)
    
    const updateData: any = {
      updatedAt: new Date().toISOString(),
    }
    
    if (updates.content !== undefined) {
      updateData.content = updates.content
    }
    
    if (updates.lotIds !== undefined) {
      updateData.lotIds = updates.lotIds
      updateData.primaryLotId = updates.lotIds[0]
    }
    
    if (updates.taggedUserIds !== undefined) {
      updateData.taggedUserIds = updates.taggedUserIds
    }
    
    await updateDoc(docRef, updateData)
    
    console.log('✅ AGC: Note updated:', noteId)
  } catch (error) {
    console.error('AGC: Failed to update note', error)
    throw error
  }
}

/**
 * AGC: Add tagged users to a note
 */
export const addTaggedUsers = async (
  noteId: string,
  userIds: string[]
): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId)
    
    await updateDoc(docRef, {
      taggedUserIds: arrayUnion(...userIds),
      updatedAt: new Date().toISOString(),
    })
    
    console.log(`✅ AGC: Added ${userIds.length} tagged users to note ${noteId}`)
  } catch (error) {
    console.error('AGC: Failed to add tagged users', error)
    throw error
  }
}

/**
 * AGC: Remove tagged users from a note
 */
export const removeTaggedUsers = async (
  noteId: string,
  userIds: string[]
): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId)
    
    await updateDoc(docRef, {
      taggedUserIds: arrayRemove(...userIds),
      updatedAt: new Date().toISOString(),
    })
    
    console.log(`✅ AGC: Removed ${userIds.length} tagged users from note ${noteId}`)
  } catch (error) {
    console.error('AGC: Failed to remove tagged users', error)
    throw error
  }
}

/**
 * AGC: Delete a note and its media
 */
export const deleteNote = async (noteId: string): Promise<void> => {
  try {
    // AGC: Get note to retrieve media URL
    const note = await getNoteById(noteId)
    
    if (!note) {
      throw new Error('Note not found')
    }
    
    // AGC: Delete media from storage if exists
    if (note.mediaUrl) {
      try {
        await deleteMedia(note.mediaUrl)
        console.log('🗑️ AGC: Media deleted')
      } catch (error) {
        console.warn('AGC: Failed to delete media, continuing with note deletion', error)
      }
    }
    
    // AGC: Delete thumbnail if exists
    if (note.mediaThumbnailUrl) {
      try {
        await deleteMedia(note.mediaThumbnailUrl)
        console.log('🗑️ AGC: Thumbnail deleted')
      } catch (error) {
        console.warn('AGC: Failed to delete thumbnail', error)
      }
    }
    
    // AGC: Delete note document
    const docRef = doc(db, NOTES_COLLECTION, noteId)
    await deleteDoc(docRef)
    
    console.log('✅ AGC: Note deleted:', noteId)
  } catch (error) {
    console.error('AGC: Failed to delete note', error)
    throw error
  }
}

/**
 * AGC: Get notes where user is tagged
 */
export const getNotesWhereTagged = async (userId: string): Promise<LotNote[]> => {
  try {
    const q = query(
      collection(db, NOTES_COLLECTION),
      where('taggedUserIds', 'array-contains', userId),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    
    const notes: LotNote[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as LotNote))
    
    console.log(`📋 AGC: Retrieved ${notes.length} notes where user is tagged`)
    
    return notes
  } catch (error) {
    console.error('AGC: Failed to get notes where tagged', error)
    throw error
  }
}

/**
 * AGC: Get timeline combining notes and tasks for a lot
 */
export const getNotesTimeline = async (
  lotId: string,
  tasks: Task[]
): Promise<TimelineEntry[]> => {
  try {
    // AGC: Get notes for this lot
    const notes = await getNotesByLot(lotId)
    
    // AGC: Convert notes to timeline entries
    const noteEntries: TimelineEntry[] = notes.map(note => ({
      id: note.id,
      type: 'note',
      timestamp: note.createdAt,
      title: getNoteTitleFromType(note.type),
      description: note.content,
      icon: getNoteIcon(note.type),
      lotIds: note.lotIds,
      userId: note.userId,
      userName: note.userName,
      noteData: note,
    }))
    
    // AGC: Convert tasks to timeline entries
    const taskEntries: TimelineEntry[] = tasks
      .filter(task => task.plotId === lotId)
      .map(task => ({
        id: task.id,
        type: 'task',
        timestamp: task.createdAt,
        title: task.title,
        description: task.description,
        icon: getTaskIcon(task.category),
        lotIds: [task.plotId],
        userId: task.createdBy,
        taskData: task,
      }))
    
    // AGC: Combine and sort by timestamp (newest first)
    const timeline = [...noteEntries, ...taskEntries].sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )
    
    console.log(`📅 AGC: Generated timeline with ${timeline.length} entries`)
    
    return timeline
  } catch (error) {
    console.error('AGC: Failed to get notes timeline', error)
    throw error
  }
}

/**
 * AGC: Helper to get note title from type
 */
const getNoteTitleFromType = (type: string): string => {
  switch (type) {
    case 'photo': return 'Photo'
    case 'video': return 'Video'
    case 'audio': return 'Audio Note'
    case 'text': return 'Text Note'
    default: return 'Note'
  }
}

/**
 * AGC: Helper to get note icon
 */
const getNoteIcon = (type: string): string => {
  switch (type) {
    case 'photo': return '📷'
    case 'video': return '🎬'
    case 'audio': return '🎤'
    case 'text': return '📝'
    default: return '📋'
  }
}

/**
 * AGC: Helper to get task icon
 */
const getTaskIcon = (category?: string): string => {
  switch (category) {
    case 'planting': return '🌱'
    case 'irrigation': return '💧'
    case 'fertilization': return '🧪'
    case 'spraying': return '💨'
    case 'harvesting': return '🌾'
    case 'maintenance': return '🔧'
    default: return '✅'
  }
}
